import * as React from 'react';

import once from 'lodash/once';

import ItemsProviderWithStore from '@common/react/components/Core/ItemsProviderWithStore/ItemsProviderWithStore';
import { useItemsProviderContext, SortingDirection } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ItemsProviderSynchronizer from '@common/react/components/Core/AdvancedItemsProvider/ItemsProviderSynchronizer';
import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';

import { UserFavorite } from '@app/objects/User';

export interface UserFavoritesProviderContext {
	state: {
		items: Array<UserFavorite>;
	},
	actions: {
		reload: () => void;
		addFavorite: (url: string, title?: string) => void;
		removeFavorite: (url: string) => void;
	}
}

export const createUserFavoritesProviderContext = once(() => React.createContext({} as UserFavoritesProviderContext));

export const useUserFavoritesProviderContext: () => UserFavoritesProviderContext = () => {
	const context = React.useContext(createUserFavoritesProviderContext());
	if (!context.state) throw 'Need User Favorites Context';
	return context;
};

const UserFavoritesProviderInner = ({ children }) => {
	const { openSuccessMessage } = useModal();

	const Context = createUserFavoritesProviderContext();
	const {
		state: { items },
		actions: {
			setItems,
			reload,
			addItem,
			save,
		},
	} = useItemsProviderContext<UserFavorite>();

	const addFavorite = (url: string, title?: string) => {
		const isNotExist = items.map((favorite) => favorite.url).indexOf(url) === -1;
		if (isNotExist) {
			const newItem = addItem({
				url,
				title,
			});
			save(newItem).then((res) => {
				setItems((prev) => prev.map((item) => (item.id === newItem.id ? res : item)));
				openSuccessMessage?.('Favorite is Added');
			});
		}
	};

	const removeFavorite = (url: string) => {
		const id = items.find((userFavorite) => userFavorite.url === url).id;

		save({ id, deleted: true })
			.then(() => {
				setItems((prev) => {
					return prev.filter((userFavorite) => userFavorite.url !== url);
				});
				openSuccessMessage?.('Favorite is Removed');
			});
	};

	const value = {
		state: {
			items,
		},
		actions: {
			reload,
			addFavorite,
			removeFavorite,
		},
	};

	return <Context.Provider value={value}>
		{children}
	</Context.Provider>;
};

const storageName = 'userFavorites';
const objectType = 'UserFavorite';

const UserFavoritesProvider: React.FC = ({ children }) => {
	const { getUser } = useApplicationContext();
	const user = getUser();
	return <ItemsProviderWithStore<UserFavorite>
		defaultSort={['sortOrder', SortingDirection.Ascending]}
		filterHandler={() => false}
		storeName={storageName}
		loadRequest="userFavorite"
		pagination={{
			pageSize: 100,
			current: 1,
		}}
		items={!user ? [] : undefined}
	>
		{() => <>
			<ItemsProviderSynchronizer<UserFavorite>
				objectType={objectType}
				objectSubtype=" "
				customHandler={() => {
					return undefined;
				}}
				getItemsFromInit={(notification) => notification.data.userFavorites}
				handle={(notification, { state: { items }, actions: { setItems } }) => {
					if (notification.objectType === objectType) {
						const data = notification.data as UserFavorite;

						if (notification.action === NotificationAction.Add
							&& !items.some((q) => (q.url === data.url))) {
							setItems((prev) => prev.concat(data));
						} else if (notification.action === NotificationAction.Delete) {
							setItems((prev) => prev.filter((item) => item.id !== data.id));
						}
					}
				}}
			/>
			<UserFavoritesProviderInner>
				{children}
			</UserFavoritesProviderInner>
		</>}
	</ItemsProviderWithStore>;
};

export default UserFavoritesProvider;
